import { AnsweringClient } from "../clients/answeringClient";
import { copyToClipboard } from "../utils/clipboard";
import { environment } from "../utils/environment";

export interface IAssignment {
  applicationId: number;
  title: string;
  dueDate: Date;
  instrumentTitle: string;
  doneApplicationCount: number;
  notDoneApplicationCount: number;
  totalApplicationCount: number;
  instrumentId?: number;
  allowAuthenticatedLink:Boolean
  allowAnonymousLink:Boolean
}

export interface IAssignmentToken {
  token: string;
}

export interface ManagedAssignmentsControllerDelegate {
  didGetAssignments(assignments: IAssignment[]): void;
  filter({
    page,
    total,
    filter,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void;
  applicationShouldLogout(): void;
  genericError(): void;
}

export class ManagedAssignmentsController {
  constructor(
    private delegate: ManagedAssignmentsControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getManagedAssignments(
    accessToken: string,
    options?: { page: number; limit: number }
  ) {
    try {
      const response = await this.answeringClient.getManagedAssignments(
        accessToken,
        options
      );
      const data = response.data.data as IAssignment[];
      const mappedData = data.map((assignment) => {
        return {
          ...assignment,
          dueDate: new Date(assignment.dueDate),
        };
      });
      this.delegate.didGetAssignments(mappedData);
      this.delegate.filter({
        page: response.data.page,
        total: response.data.total,
        filter: response.data.filter || "",
      });
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        this.getManagedAssignments(newTokens.accessToken);
        return;
      }
      this.delegate.genericError();
    }
  }

  public async getAnonAssignmentToken(
    accessToken: string,
    assignmentId: number
  ): Promise<string | undefined> {
    try {
      const response = await this.answeringClient.getAnonAssignmentToken(
        accessToken,
        assignmentId
      );
      const data = response.data as IAssignmentToken;
      const link = `${environment.instrumentsFrontendBaseUrl}/anon?anon_token=${data.token}`;
      await copyToClipboard(link);
      return link;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          this.delegate.genericError();
          return;
        }
        return this.getAnonAssignmentToken(newTokens.accessToken, assignmentId);
      }
      this.delegate.genericError();
      return;
    }
  }

  public async getGenericAnonAssignmentToken(
    accessToken: string,
    assignmentId: number
  ): Promise<string | undefined> {
    try {
      const response = await this.answeringClient.getGenericAnonAssignmentToken(
        accessToken,
        assignmentId
      );
      const data = response.data as IAssignmentToken;
      const link = `${environment.instrumentsFrontendBaseUrl}/genericAnon?generic_anon_token=${data.token}`;
      await copyToClipboard(link);
      return link;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          this.delegate.genericError();
          return;
        }
        return this.getGenericAnonAssignmentToken(
          newTokens.accessToken,
          assignmentId
        );
      }
      this.delegate.genericError();
      return;
    }
  }

  public async getAssignmentToken(
    accessToken: string,
    assignmentId: number
  ): Promise<string | undefined> {
    try {
      const response = await this.answeringClient.getAssignmentToken(
        accessToken,
        assignmentId
      );
      const data = response.data as IAssignmentToken;
      const link = `${environment.instrumentsFrontendBaseUrl}/invite?invite_token=${data.token}`;
      await copyToClipboard(link);
      return link;
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          this.delegate.genericError();
          return;
        }
        return this.getAssignmentToken(newTokens.accessToken, assignmentId);
      }
      this.delegate.genericError();
      return;
    }
  }

  public async updateAssignmentDueDate(
    accessToken: string,
    applicationId: number,
    dueDate: Date
  ): Promise<void> {
    try {
      await this.answeringClient.updateAssignmentDueDate(
        accessToken,
        applicationId,
        dueDate
      );
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          throw new Error("Não foi possível atualizar o prazo.");
        }
        return this.updateAssignmentDueDate(
          newTokens.accessToken,
          applicationId,
          dueDate
        );
      }
      throw new Error(this.extractErrorMessage(error));
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }

  private extractErrorMessage(error: any): string {
    const responseData = error?.response?.data;
    if (typeof responseData === "string") {
      return responseData;
    }
    if (responseData?.message) {
      if (typeof responseData.message === "string") {
        return responseData.message;
      }
      try {
        return JSON.stringify(responseData.message);
      } catch {
        return String(responseData.message);
      }
    }
    if (responseData) {
      try {
        return JSON.stringify(responseData);
      } catch {
        return String(responseData);
      }
    }
    if (error?.message) {
      return error.message;
    }
    return "Erro desconhecido ao atualizar o prazo.";
  }
}
